import { useEffect, useRef, useState } from 'react';

/**
 * Drives one horizontal scroll-snap track that shows items a page at a time.
 * Shared by the day row and the time row: it owns the page state, the arrow
 * scroll, the swipe-to-page sync, the breakpoint resize guard and the edge-fade
 * flags. The caller renders the pages, arrows and dots from what it returns.
 *
 * @param itemCount total number of items across all pages
 * @param pageSize items shown per page
 * @param activeIndex index of the selected item, used to seed and re-anchor the page
 * @returns { currentPage, pageCount, trackRef, goToPage, goToIndexPage, onTrackScroll, edgeCut }
 */
const usePagedTrack = ({ itemCount, pageSize, activeIndex }) => {
  const pageCount = Math.max(1, Math.ceil(itemCount / pageSize));
  const [page, setPage] = useState(
    Math.floor(Math.max(0, activeIndex) / pageSize)
  );
  // A breakpoint change re-slices the pages, so clamp to one that still exists.
  const currentPage = Math.min(page, pageCount - 1);
  const trackRef = useRef(null);
  const scrollSettleTimer = useRef(null);
  const [edgeCut, setEdgeCut] = useState({ left: false, right: false });

  // One page's scroll distance: the page is narrower than the track so the next
  // page peeks, so the snap step is the page's own width.
  const pageStep = (track) =>
    track.firstChild?.offsetWidth || track.clientWidth;

  // Fade the side where an item is partly scrolled out of view. jsdom reports 0
  // rects, so nothing fades there.
  const refreshEdgeCut = (track) => {
    const bounds = track.getBoundingClientRect();
    let left = false;
    let right = false;
    track.querySelectorAll('[data-pager-item]').forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.left < bounds.left - 1 && rect.right > bounds.left + 1) {
        left = true;
      }
      if (rect.right > bounds.right + 1 && rect.left < bounds.right - 1) {
        right = true;
      }
    });
    setEdgeCut((current) =>
      current.left === left && current.right === right
        ? current
        : { left, right }
    );
  };

  // True once the user has paged by an arrow, a day click, or a swipe. Only then
  // does moving the track animate; mount and breakpoint-driven re-pages jump.
  const userPaged = useRef(false);

  // Move the track to the current page. Guarded because jsdom has no scrollTo and
  // reports a 0 width; skipped when already there so it does not fight a settling
  // swipe.
  useEffect(() => {
    const track = trackRef.current;
    const step = track ? pageStep(track) : 0;
    if (!track || typeof track.scrollTo !== 'function' || !step) {
      return;
    }
    const target = currentPage * step;
    if (track.scrollLeft !== target) {
      // Mount and breakpoint-driven re-pages (useMediaQuery flips daysPerPage on
      // its second render, which re-pages through the resize guard) must jump, not
      // slide. Only a real paging action the user took animates. 'instant' also
      // overrides the track's CSS scroll-behavior: smooth, which 'auto' would not.
      track.scrollTo({
        left: target,
        behavior: userPaged.current ? 'smooth' : 'instant',
      });
    }
    refreshEdgeCut(track);
  }, [currentPage]);

  // Sync the page back from a swipe once the scroll settles, so the arrows, dots
  // and fade follow the items. Debounced so an arrow's smooth scroll is not read
  // mid-animation.
  const onTrackScroll = () => {
    const track = trackRef.current;
    const step = track ? pageStep(track) : 0;
    if (!track || !step) {
      return;
    }
    clearTimeout(scrollSettleTimer.current);
    scrollSettleTimer.current = setTimeout(() => {
      const nextPage = Math.round(track.scrollLeft / step);
      // Compare against the live page inside the updater, not the page captured
      // when this handler was created: a mount-then-breakpoint re-page can fire a
      // settle whose captured page is stale, which would wrongly flag user paging.
      setPage((current) => {
        if (current === nextPage) {
          return current;
        }
        userPaged.current = true;
        return nextPage;
      });
      refreshEdgeCut(track);
    }, 120);
  };

  useEffect(() => () => clearTimeout(scrollSettleTimer.current), []);

  // On a breakpoint change (e.g. a phone rotating) snap back to the page holding
  // the active item, not a stale scroll position.
  useEffect(() => {
    setPage(Math.floor(Math.max(0, activeIndex) / pageSize));
    // activeIndex is intentionally omitted: this guards the page-size change, not
    // every active-item change (handled where the item is picked).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const goToPage = (next) => {
    userPaged.current = true;
    setPage(Math.max(0, Math.min(pageCount - 1, next)));
  };

  // Bring an item's page into view (e.g. clicking a part-hidden peek item).
  const goToIndexPage = (index) => {
    userPaged.current = true;
    setPage(Math.floor(index / pageSize));
  };

  return {
    currentPage,
    pageCount,
    trackRef,
    goToPage,
    goToIndexPage,
    onTrackScroll,
    edgeCut,
  };
};

export default usePagedTrack;
